import React, { useMemo, useState } from 'react';
import { FileTextIcon, PieChartIcon, ListChecksIcon, InfoIcon, LayoutGridIcon, LayersIcon, SparklesIcon, ImageIcon, ZapIcon, PlusIcon, MinusIcon, Trash2Icon, AlertTriangleIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
// Build a map from topic name -> list of source-gap notes that relate to it.
// Uses simple keyword overlap between each note and each topic title; any
// note that doesn't clearly match a topic falls back to the first topic.
const buildGapsByTopic = (notes: string[] | undefined, topics: {
  topic: string;
}[] | undefined): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  if (!notes || !topics || topics.length === 0) return map;
  const STOP = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'to', 'in', 'on', 'for', 'with', 'is', 'are', 'was', 'were', 'that', 'this', 'these', 'those', 'it', 'as', 'at', 'by', 'from', 'be', 'will', 'no', 'not', 'did', 'does', 'do', 'include', 'specific', 'available', 'provided', 'source', 'generated', 'content', 'uses', 'general', 'examples']);
  const tokenize = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 2 && !STOP.has(w));
  const topicTokens = topics.map(t => ({
    topic: t.topic,
    tokens: new Set(tokenize(t.topic))
  }));
  notes.forEach(note => {
    const noteTokens = tokenize(note);
    let best = topicTokens[0];
    let bestScore = 0;
    topicTokens.forEach(t => {
      const score = noteTokens.reduce((acc, tok) => acc + (t.tokens.has(tok) ? 1 : 0), 0);
      if (score > bestScore) {
        bestScore = score;
        best = t;
      }
    });
    if (!map[best.topic]) map[best.topic] = [];
    map[best.topic].push(note);
  });
  return map;
};
// Subtle inline indicator + tooltip used to surface source gaps next to the
// topic the gap relates to. Replaces the previous top-of-page amber banner.
const SourceGapIndicator = ({
  notes
}: {
  notes: string[];
}) => {
  if (notes.length === 0) return null;
  return <Tooltip.Root data-id="element-709">
      <Tooltip.Trigger asChild data-id="element-710">
        <button type="button" aria-label={`${notes.length} source gap${notes.length > 1 ? 's' : ''} flagged`} className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 transition-colors" data-id="element-711">
          <AlertTriangleIcon size={12} data-id="element-712" />
          <span className="text-[10px] font-semibold normal-case tracking-normal" data-id="element-713">
            {notes.length} source gap{notes.length > 1 ? 's' : ''}
          </span>
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal data-id="element-714">
        <Tooltip.Content side="top" align="start" sideOffset={6} className="z-50 max-w-xs bg-white border border-amber-200 rounded-lg shadow-lg px-3 py-2.5" data-id="element-715">
          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1.5" data-id="element-716">
            Source Gap{notes.length > 1 ? 's' : ''} Flagged
          </p>
          <ul className="space-y-1.5" data-id="element-717">
            {notes.map((note, i) => <li key={i} className="flex items-start gap-1.5" data-id="element-718">
                <div className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" data-id="element-719" />
                <p className="text-xs text-gray-700 leading-snug" data-id="element-720">{note}</p>
              </li>)}
          </ul>
          <Tooltip.Arrow className="fill-white" data-id="element-721" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>;
};
export interface TopicDistribution {
  topic: string;
  weight_percent: number;
  question_count: number;
}
export interface QuestionPlanItem {
  index: number;
  question_type: string;
  taxonomy: string;
  core_concept: string;
  intent: string;
}
export interface CardPlanItem {
  index: number;
  type: 'L' | 'A' | 'R' | 'P';
  mode: 'text' | 'text-image' | 'interactive';
  title: string;
  topic: string;
  intent?: string;
  questionCount?: number;
}
export interface PlannerData {
  title: string;
  summary: string[];
  source_notes?: string[];
  total_cards?: number;
  total_learning_cards?: number;
  coverage?: {
    total_questions: number;
    topic_distribution: TopicDistribution[];
  };
  question_plan?: QuestionPlanItem[];
  topic_breakdown?: TopicDistribution[];
  card_plan?: CardPlanItem[];
}
export interface BlueprintPlannerProps {
  data: PlannerData;
  onDataChange: (data: PlannerData) => void;
}
const taxonomyMap: Record<string, {
  label: string;
  color: string;
  bg: string;
}> = {
  R: {
    label: 'Recall',
    color: 'text-gray-500',
    bg: 'bg-gray-100'
  },
  U: {
    label: 'Understand',
    color: 'text-gray-500',
    bg: 'bg-gray-100'
  },
  A: {
    label: 'Apply',
    color: 'text-gray-500',
    bg: 'bg-gray-100'
  }
};
const getTaxonomy = (code: string) => {
  return taxonomyMap[code] || {
    label: code,
    color: 'text-gray-600',
    bg: 'bg-gray-50'
  };
};
export function BlueprintPlanner({
  data,
  onDataChange
}: BlueprintPlannerProps) {
  const isPlaybook = !!data.topic_breakdown;
  const [editingTopicIndex, setEditingTopicIndex] = useState<number | null>(null);
  const [editingCardIndex, setEditingCardIndex] = useState<number | null>(null);
  const [editingIntentIndex, setEditingIntentIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editIntentValue, setEditIntentValue] = useState('');
  const gapsByTopic = useMemo(() => buildGapsByTopic(data.source_notes, data.topic_breakdown), [data.source_notes, data.topic_breakdown]);
  const handleTopicEdit = (index: number, newValue: string) => {
    if (!data.topic_breakdown) return;
    const newData = {
      ...data
    };
    const oldTopic = newData.topic_breakdown[index].topic;
    newData.topic_breakdown[index].topic = newValue;
    // Also update card plan topics to match
    if (newData.card_plan) {
      newData.card_plan = newData.card_plan.map(card => card.topic === oldTopic ? {
        ...card,
        topic: newValue
      } : card);
    }
    onDataChange(newData);
    setEditingTopicIndex(null);
  };
  const handleCardEdit = (index: number, newValue: string) => {
    if (!data.card_plan) return;
    const newData = {
      ...data
    };
    const cardIndex = newData.card_plan.findIndex(c => c.index === index);
    if (cardIndex !== -1) {
      newData.card_plan[cardIndex].title = newValue;
    }
    onDataChange(newData);
    setEditingCardIndex(null);
  };
  const handleIntentEdit = (index: number, newValue: string) => {
    if (!data.card_plan) return;
    const newData = {
      ...data
    };
    const cardIndex = newData.card_plan.findIndex(c => c.index === index);
    if (cardIndex !== -1) {
      newData.card_plan[cardIndex].intent = newValue;
    }
    onDataChange(newData);
    setEditingIntentIndex(null);
  };
  const handleTypeToggle = (index: number) => {
    if (!data.card_plan) return;
    const newData = {
      ...data
    };
    const cardIndex = newData.card_plan.findIndex(c => c.index === index);
    if (cardIndex !== -1) {
      const card = newData.card_plan[cardIndex];
      if (card.type === 'L' || card.type === 'A') {
        newData.card_plan[cardIndex].type = card.type === 'L' ? 'A' : 'L';
        // If switching to A, set mode to interactive
        if (newData.card_plan[cardIndex].type === 'A') {
          newData.card_plan[cardIndex].mode = 'interactive';
        }
      }
    }
    onDataChange(newData);
  };
  const handleModeToggle = (index: number) => {
    if (!data.card_plan) return;
    const newData = {
      ...data
    };
    const cardIndex = newData.card_plan.findIndex(c => c.index === index);
    if (cardIndex !== -1) {
      const card = newData.card_plan[cardIndex];
      // Only toggle for L cards
      if (card.type === 'L') {
        newData.card_plan[cardIndex].mode = card.mode === 'text-image' ? 'text' : 'text-image';
      }
    }
    onDataChange(newData);
  };
  const handleQuestionCountChange = (index: number, delta: number) => {
    if (!data.card_plan) return;
    const newData = {
      ...data
    };
    const cardIndex = newData.card_plan.findIndex(c => c.index === index);
    if (cardIndex !== -1) {
      const card = newData.card_plan[cardIndex];
      if (card.type === 'P') {
        const currentCount = card.questionCount || 0;
        const newCount = Math.max(1, currentCount + delta);
        newData.card_plan[cardIndex].questionCount = newCount;
      }
    }
    onDataChange(newData);
  };
  const handleCardRemove = (index: number) => {
    if (!data.card_plan) return;
    const newData = {
      ...data
    };
    newData.card_plan = newData.card_plan.filter(c => c.index !== index);
    onDataChange(newData);
  };
  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'text-image':
        return ImageIcon;
      case 'interactive':
        return ZapIcon;
      default:
        return FileTextIcon;
    }
  };
  const getTypeBadgeStyle = (type: string) => {
    switch (type) {
      case 'L':
        return 'bg-[#2563EB]/10 text-[#2563EB]';
      case 'A':
        return 'bg-[#2563EB]/10 text-[#2563EB]';
      case 'R':
        return 'bg-amber-500/10 text-amber-600';
      case 'P':
        return 'bg-[#00B75F]/10 text-[#00B75F]';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };
  return <Tooltip.Provider delayDuration={150} data-id="element-722">
      <div className="flex-1 overflow-y-auto bg-gray-50/50 h-full" data-id="element-723">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-5" data-id="element-724">
          {/* Title */}
          <div data-id="element-725">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-726">
              {data.title}
            </h2>
            <p className="text-[11px] sm:text-xs text-gray-400 mt-1 flex items-center gap-1" data-id="element-727">
              <InfoIcon size={11} data-id="element-728" />
              Read-only preview — use the prompt to regenerate
            </p>
          </div>

          {/* Coverage / Topic Breakdown */}
          {isPlaybook && data.topic_breakdown ? <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-729">
              <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-730">
                <div className="flex items-center gap-2.5" data-id="element-731">
                  <div className="w-6 h-6 rounded-md bg-purple-50 flex items-center justify-center" data-id="element-732">
                    <LayoutGridIcon size={13} className="text-purple-600" data-id="element-733" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-734">
                    Topic Breakdown
                  </h3>
                </div>
                {(() => {
              const learningCards = data.total_learning_cards ?? (data.card_plan ? data.card_plan.filter(c => c.type === 'L' || c.type === 'A' || c.type === 'R').length : 0);
              return <span className="text-sm font-bold text-purple-600" data-id="element-735">
                      {learningCards} learning cards
                    </span>;
            })()}
              </div>
              <div className="divide-y divide-gray-100" data-id="element-736">
                {data.topic_breakdown.map((topic, i) => {
              const topicCardCount = data.card_plan ? data.card_plan.filter(c => c.topic === topic.topic && (c.type === 'L' || c.type === 'A' || c.type === 'R')).length : topic.question_count;
              return <div key={i} className="px-4 sm:px-5 py-3 sm:py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 group" data-id="element-737">
                      {editingTopicIndex === i ? <input autoFocus className="text-sm text-gray-800 font-medium bg-white border border-purple-300 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-purple-100 w-full sm:w-1/2" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => handleTopicEdit(i, editValue)} onKeyDown={e => e.key === 'Enter' && handleTopicEdit(i, editValue)} data-id="element-738" /> : <span className="text-sm text-gray-800 font-medium cursor-pointer hover:text-purple-600 transition-colors border-b border-transparent hover:border-purple-200" onClick={() => {
                  setEditValue(topic.topic);
                  setEditingTopicIndex(i);
                }} data-id="element-739">
                          {topic.topic}
                        </span>}
                      <div className="flex items-center justify-between sm:justify-end gap-4" data-id="element-740">
                        <div className="flex items-center gap-2 w-full sm:w-32" data-id="element-741">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-742">
                            <div className="h-full bg-purple-500 rounded-full" style={{
                        width: `${Math.min(topic.weight_percent, 100)}%`
                      }} data-id="element-743" />
                          </div>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-744">
                          <span className="text-xs font-semibold text-gray-500 w-12 text-right" data-id="element-745">
                            {topicCardCount} cards
                          </span>
                        </div>
                      </div>
                    </div>;
            })}
                <div className="px-4 sm:px-5 py-3 bg-gray-50/30" data-id="element-746">
                  <p className="text-xs text-gray-500" data-id="element-747">
                    Each topic includes a recap card and practice questions.
                  </p>
                </div>
              </div>
            </div> : data.coverage && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-748">
                <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-749">
                  <div className="flex items-center gap-2.5" data-id="element-750">
                    <div className="w-6 h-6 rounded-md bg-purple-50 flex items-center justify-center" data-id="element-751">
                      <PieChartIcon size={13} className="text-purple-600" data-id="element-752" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900" data-id="element-753">
                      Coverage
                    </h3>
                  </div>
                  <span className="text-sm font-bold text-purple-600" data-id="element-754">
                    {data.coverage.total_questions} questions
                  </span>
                </div>
                <div className="divide-y divide-gray-100" data-id="element-755">
                  {data.coverage.topic_distribution.map((topic, i) => <div key={i} className="px-4 sm:px-5 py-3 sm:py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0" data-id="element-756">
                      <span className="text-sm text-gray-800 font-medium" data-id="element-757">
                        {topic.topic}
                      </span>
                      <div className="flex items-center justify-between sm:justify-end gap-4" data-id="element-758">
                        <div className="flex items-center gap-2 w-full sm:w-32" data-id="element-759">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-760">
                            <div className="h-full bg-purple-500 rounded-full" style={{
                      width: `${Math.min(topic.weight_percent, 100)}%`
                    }} data-id="element-761" />
                          </div>
                          <span className="text-xs text-gray-500 font-medium w-8 text-right" data-id="element-762">
                            {topic.weight_percent}%
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-gray-500 w-6 text-right" data-id="element-763">
                          {topic.question_count}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>}

          {/* Question Plan / Card Plan */}
          {isPlaybook && data.card_plan ? <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-764">
              <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2.5" data-id="element-765">
                <div className="w-6 h-6 rounded-md bg-[#2563EB]/10 flex items-center justify-center" data-id="element-766">
                  <LayersIcon size={13} className="text-[#2563EB]" data-id="element-767" />
                </div>
                <h3 className="text-sm font-bold text-gray-900" data-id="element-768">Card Plan</h3>
              </div>

              {/* Legend */}
              <div className="px-4 sm:px-5 py-2 border-b border-gray-100 flex items-center gap-3 flex-wrap" data-id="element-769">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-770">
                  TYPE:
                </span>
                <div className="flex items-center gap-1" data-id="element-771">
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#2563EB]/10 text-[8px] font-bold text-[#2563EB]" data-id="element-772">
                    L
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-773">
                    Learning Card
                  </span>
                </div>
                <div className="flex items-center gap-1" data-id="element-774">
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#2563EB]/10 text-[8px] font-bold text-[#2563EB]" data-id="element-775">
                    A
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-776">Activity</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-777">
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-500/10 text-[8px] font-bold text-amber-600" data-id="element-778">
                    R
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-779">Recap</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-780">
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#00B75F]/10 text-[8px] font-bold text-[#00B75F]" data-id="element-781">
                    P
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-782">Practice</span>
                </div>
                <span className="text-[10px] text-gray-300" data-id="element-783">|</span>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-784">
                  MODE:
                </span>
                <div className="flex items-center gap-1" data-id="element-785">
                  <ImageIcon size={10} className="text-gray-400" data-id="element-786" />
                  <span className="text-[10px] text-gray-400" data-id="element-787">
                    Text + Image
                  </span>
                </div>
                <div className="flex items-center gap-1" data-id="element-788">
                  <FileTextIcon size={10} className="text-gray-400" data-id="element-789" />
                  <span className="text-[10px] text-gray-400" data-id="element-790">Text only</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-791">
                  <ZapIcon size={10} className="text-gray-400" data-id="element-792" />
                  <span className="text-[10px] text-gray-400" data-id="element-793">Interactive</span>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block" data-id="element-794">
                {data.topic_breakdown?.map((topic, topicIdx) => {
              const topicCards = data.card_plan?.filter(c => c.topic === topic.topic) || [];
              let serialNumber = 0;
              return <div key={topicIdx} className="border-b border-gray-100 last:border-b-0" data-id="element-795">
                      <div className="px-4 sm:px-5 py-2.5 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between gap-3" data-id="element-796">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-797">
                          Topic {topicIdx + 1} — {topic.topic}
                        </h4>
                        {gapsByTopic[topic.topic] && <SourceGapIndicator notes={gapsByTopic[topic.topic]} data-id="element-798" />}
                      </div>
                      <table className="w-full" data-id="element-799">
                        <thead data-id="element-800">
                          <tr className="border-b border-gray-100 bg-gray-50/30" data-id="element-801">
                            <th className="pl-5 pr-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-10 text-left" data-id="element-802">
                              #
                            </th>
                            <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-16 text-left" data-id="element-803">
                              Type
                            </th>
                            <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-left" data-id="element-804">
                              Concept Name
                            </th>
                            <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-left" data-id="element-805">
                              Intent
                            </th>
                            <th className="w-8 pr-3" data-id="element-806"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50" data-id="element-807">
                          {topicCards.map(card => {
                      if (card.type === 'L' || card.type === 'A') {
                        serialNumber++;
                      }
                      const currentSerial = card.type === 'L' || card.type === 'A' ? serialNumber : null;
                      const ModeIcon = getModeIcon(card.mode);
                      return <tr key={card.index} className="group hover:bg-gray-50/50 transition-colors" data-id="element-808">
                                <td className="pl-5 pr-2 py-2" data-id="element-809">
                                  {currentSerial !== null ? <span className="text-xs font-bold text-gray-400" data-id="element-810">
                                      {currentSerial}
                                    </span> : <span className="text-xs text-gray-300" data-id="element-811">
                                      —
                                    </span>}
                                </td>
                                <td className="px-2 py-2" data-id="element-812">
                                  <button onClick={() => handleTypeToggle(card.index)} disabled={card.type === 'R' || card.type === 'P'} className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold transition-colors ${getTypeBadgeStyle(card.type)} ${card.type === 'L' || card.type === 'A' ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`} data-id="element-813">
                                    {card.type}
                                  </button>
                                </td>
                                <td className="px-2 py-2" data-id="element-814">
                                  <div className="flex items-center gap-2" data-id="element-815">
                                    <button onClick={() => handleModeToggle(card.index)} disabled={card.type !== 'L'} className={`flex-shrink-0 ${card.type === 'L' ? 'cursor-pointer text-gray-400 hover:text-gray-600' : 'cursor-default text-gray-400'}`} data-id="element-816">
                                      <ModeIcon size={13} data-id="element-817" />
                                    </button>
                                    {editingCardIndex === card.index ? <input autoFocus className="text-sm text-gray-800 bg-white border border-[#2563EB]/30 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-[#2563EB]/20 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => handleCardEdit(card.index, editValue)} onKeyDown={e => e.key === 'Enter' && handleCardEdit(card.index, editValue)} data-id="element-818" /> : <div className="flex items-center gap-2" data-id="element-819">
                                        <span className={`text-sm text-gray-800 ${card.type === 'L' || card.type === 'A' ? 'cursor-pointer hover:text-[#2563EB] border-b border-transparent hover:border-[#2563EB]/30 transition-colors' : ''}`} onClick={() => {
                                if (card.type === 'L' || card.type === 'A') {
                                  setEditValue(card.title);
                                  setEditingCardIndex(card.index);
                                }
                              }} data-id="element-820">
                                          {card.title}
                                        </span>
                                        {card.type === 'R' && (() => {
                                const topicCards = data.card_plan?.filter(c => c.topic === card.topic && (c.type === 'L' || c.type === 'A' || c.type === 'R')) || [];
                                return <span className="text-xs text-gray-400 font-medium" data-id="element-821">
                                                {topicCards.length} cards
                                              </span>;
                              })()}
                                      </div>}
                                  </div>
                                </td>
                                <td className="px-2 py-2" data-id="element-822">
                                  {card.type === 'P' ? <div className="flex items-center gap-2" data-id="element-823">
                                      <button onClick={() => handleQuestionCountChange(card.index, -1)} className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors" data-id="element-824">
                                        <MinusIcon size={12} data-id="element-825" />
                                      </button>
                                      <span className="text-xs text-gray-600 font-medium min-w-[80px] text-center" data-id="element-826">
                                        {card.questionCount || 0} questions
                                      </span>
                                      <button onClick={() => handleQuestionCountChange(card.index, 1)} className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors" data-id="element-827">
                                        <PlusIcon size={12} data-id="element-828" />
                                      </button>
                                    </div> : editingIntentIndex === card.index ? <input autoFocus className="text-xs text-gray-600 italic bg-white border border-[#2563EB]/30 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-[#2563EB]/20 w-full" value={editIntentValue} onChange={e => setEditIntentValue(e.target.value)} onBlur={() => handleIntentEdit(card.index, editIntentValue)} onKeyDown={e => e.key === 'Enter' && handleIntentEdit(card.index, editIntentValue)} data-id="element-829" /> : <span className={`text-xs text-gray-400 italic ${card.type === 'L' || card.type === 'A' ? 'cursor-pointer hover:text-gray-600 transition-colors' : ''}`} onClick={() => {
                            if ((card.type === 'L' || card.type === 'A') && card.intent) {
                              setEditIntentValue(card.intent);
                              setEditingIntentIndex(card.index);
                            }
                          }} data-id="element-830">
                                      {card.intent || ''}
                                    </span>}
                                </td>
                                <td className="pr-3 py-2" data-id="element-831">
                                  <button onClick={() => handleCardRemove(card.index)} className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all" title="Remove card" data-id="element-832">
                                    <Trash2Icon size={13} data-id="element-833" />
                                  </button>
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>;
            })}
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-100" data-id="element-834">
                {data.topic_breakdown?.map((topic, topicIdx) => {
              const topicCards = data.card_plan?.filter(c => c.topic === topic.topic) || [];
              let serialNumber = 0;
              return <div key={topicIdx} data-id="element-835">
                      <div className="px-4 py-2.5 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between gap-2" data-id="element-836">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-837">
                          Topic {topicIdx + 1} — {topic.topic}
                        </h4>
                        {gapsByTopic[topic.topic] && <SourceGapIndicator notes={gapsByTopic[topic.topic]} data-id="element-838" />}
                      </div>
                      <div className="divide-y divide-gray-50" data-id="element-839">
                        {topicCards.map(card => {
                    if (card.type === 'L' || card.type === 'A') {
                      serialNumber++;
                    }
                    const currentSerial = card.type === 'L' || card.type === 'A' ? serialNumber : null;
                    const ModeIcon = getModeIcon(card.mode);
                    return <div key={card.index} className="px-4 py-2.5" data-id="element-840">
                              <div className="flex items-start gap-2" data-id="element-841">
                                <span className="text-[11px] font-bold text-gray-400 w-4 flex-shrink-0 text-right mt-0.5" data-id="element-842">
                                  {currentSerial !== null ? currentSerial : '—'}
                                </span>
                                <button onClick={() => handleTypeToggle(card.index)} disabled={card.type === 'R' || card.type === 'P'} className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0 transition-colors ${getTypeBadgeStyle(card.type)} ${card.type === 'L' || card.type === 'A' ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-843">
                                  {card.type}
                                </button>
                                <div className="flex-1 min-w-0" data-id="element-844">
                                  <div className="flex items-center gap-1.5 mb-1" data-id="element-845">
                                    <button onClick={() => handleModeToggle(card.index)} disabled={card.type !== 'L'} className={`flex-shrink-0 ${card.type === 'L' ? 'cursor-pointer' : 'cursor-default'} text-gray-400`} data-id="element-846">
                                      <ModeIcon size={12} data-id="element-847" />
                                    </button>
                                    {editingCardIndex === card.index ? <input autoFocus className="text-[13px] text-gray-800 bg-white border border-[#2563EB]/30 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-[#2563EB]/20 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => handleCardEdit(card.index, editValue)} onKeyDown={e => e.key === 'Enter' && handleCardEdit(card.index, editValue)} data-id="element-848" /> : <div className="flex items-center gap-1.5 flex-wrap" data-id="element-849">
                                        <span className={`text-[13px] font-medium text-gray-900 leading-tight ${card.type === 'L' || card.type === 'A' ? 'cursor-pointer' : ''}`} onClick={() => {
                                if (card.type === 'L' || card.type === 'A') {
                                  setEditValue(card.title);
                                  setEditingCardIndex(card.index);
                                }
                              }} data-id="element-850">
                                          {card.title}
                                        </span>
                                        {card.type === 'R' && (() => {
                                const topicCards = data.card_plan?.filter(c => c.topic === card.topic && (c.type === 'L' || c.type === 'A' || c.type === 'R')) || [];
                                return <span className="text-[11px] text-gray-400 font-medium" data-id="element-851">
                                                {topicCards.length} cards
                                              </span>;
                              })()}
                                      </div>}
                                  </div>
                                  {card.type === 'P' ? <div className="flex items-center gap-2 mt-1" data-id="element-852">
                                      <button onClick={() => handleQuestionCountChange(card.index, -1)} className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 text-gray-600" data-id="element-853">
                                        <MinusIcon size={11} data-id="element-854" />
                                      </button>
                                      <span className="text-[11px] text-gray-600 font-medium" data-id="element-855">
                                        {card.questionCount || 0} questions
                                      </span>
                                      <button onClick={() => handleQuestionCountChange(card.index, 1)} className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 text-gray-600" data-id="element-856">
                                        <PlusIcon size={11} data-id="element-857" />
                                      </button>
                                    </div> : editingIntentIndex === card.index ? <input autoFocus className="text-[11px] text-gray-600 italic bg-white border border-[#2563EB]/30 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-[#2563EB]/20 w-full mt-1" value={editIntentValue} onChange={e => setEditIntentValue(e.target.value)} onBlur={() => handleIntentEdit(card.index, editIntentValue)} onKeyDown={e => e.key === 'Enter' && handleIntentEdit(card.index, editIntentValue)} data-id="element-858" /> : <p className={`text-[11px] text-gray-400 leading-tight mt-0.5 italic ${card.type === 'L' || card.type === 'A' ? 'cursor-pointer' : ''}`} onClick={() => {
                            if ((card.type === 'L' || card.type === 'A') && card.intent) {
                              setEditIntentValue(card.intent);
                              setEditingIntentIndex(card.index);
                            }
                          }} data-id="element-859">
                                      {card.intent || ''}
                                    </p>}
                                </div>
                                <button onClick={() => handleCardRemove(card.index)} className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0" title="Remove card" data-id="element-860">
                                  <Trash2Icon size={12} data-id="element-861" />
                                </button>
                              </div>
                            </div>;
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </div> : data.question_plan && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-862">
                <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2.5" data-id="element-863">
                  <div className="w-6 h-6 rounded-md bg-[#00B75F]/10 flex items-center justify-center" data-id="element-864">
                    <ListChecksIcon size={13} className="text-[#00B75F]" data-id="element-865" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-866">
                    Question Plan
                  </h3>
                </div>

                {/* Taxonomy Legend */}
                <div className="px-4 sm:px-5 py-2 border-b border-gray-100 flex items-center gap-3 flex-wrap" data-id="element-867">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-868">
                    Level:
                  </span>
                  {Object.entries(taxonomyMap).map(([code, {
              label
            }]) => <div key={code} className="flex items-center gap-1" data-id="element-869">
                      <span className="text-[10px] font-bold text-gray-400" data-id="element-870">
                        {code}
                      </span>
                      <span className="text-[10px] text-gray-400" data-id="element-871">
                        = {label}
                      </span>
                    </div>)}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block" data-id="element-872">
                  <table className="w-full text-left" data-id="element-873">
                    <thead data-id="element-874">
                      <tr className="border-b border-gray-100 bg-gray-50/30" data-id="element-875">
                        <th className="pl-5 pr-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-10" data-id="element-876">
                          #
                        </th>
                        <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-16" data-id="element-877">
                          Type
                        </th>
                        <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-20 text-center" data-id="element-878">
                          Level
                        </th>
                        <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-879">
                          Core Concept
                        </th>
                        <th className="px-2 pr-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-880">
                          Intent
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50" data-id="element-881">
                      {data.question_plan.map(q => {
                  const tax = getTaxonomy(q.taxonomy);
                  return <tr key={q.index} className="hover:bg-gray-50/50 transition-colors" data-id="element-882">
                            <td className="pl-5 pr-2 py-2" data-id="element-883">
                              <span className="text-xs font-bold text-gray-400" data-id="element-884">
                                {q.index}
                              </span>
                            </td>
                            <td className="px-2 py-2" data-id="element-885">
                              <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-[11px] font-semibold text-gray-600" data-id="element-886">
                                {q.question_type}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center" data-id="element-887">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-[11px] font-bold text-gray-500" data-id="element-888">
                                {q.taxonomy}
                              </span>
                            </td>
                            <td className="px-2 py-2" data-id="element-889">
                              <span className="text-sm text-gray-800" data-id="element-890">
                                {q.core_concept}
                              </span>
                            </td>
                            <td className="px-2 pr-5 py-2" data-id="element-891">
                              <span className="text-xs text-gray-400" data-id="element-892">
                                {q.intent}
                              </span>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100" data-id="element-893">
                  {data.question_plan.map(q => {
              const tax = getTaxonomy(q.taxonomy);
              return <div key={q.index} className="px-4 py-2.5 flex items-center gap-2.5" data-id="element-894">
                        <span className="text-[11px] font-bold text-gray-400 w-4 flex-shrink-0 text-right" data-id="element-895">
                          {q.index}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-896">
                          <p className="text-[13px] font-medium text-gray-900 leading-tight truncate" data-id="element-897">
                            {q.core_concept}
                          </p>
                          <p className="text-[11px] text-gray-400 leading-tight mt-0.5 truncate italic" data-id="element-898">
                            {q.intent}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-899">
                          <span className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-semibold text-gray-500" data-id="element-900">
                            {q.question_type}
                          </span>
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-[10px] font-bold text-gray-500" data-id="element-901">
                            {q.taxonomy}
                          </span>
                        </div>
                      </div>;
            })}
                </div>
              </div>}
        </div>
      </div>
    </Tooltip.Provider>;
}